import { trackMicrosoftConversion } from "./microsoftConversions";
/**
 * Analytics + UTM capture for Valor Tactical.
 *
 * Why this file exists:
 *  - PostHog is wired sitewide for session recordings & identified profiles.
 *  - We also want explicit conversion events (lead_submit, rfq_submit, etc.)
 *    so the team can see *what* converts, not just *who* visits.
 *  - When marketing wires GA4 or LinkedIn Insight Tag via .env, those
 *    additional sinks light up automatically — no code change required.
 *  - UTMs are captured on first paint and attached to every form submit so
 *    leads carry their acquisition source through to the CRM email.
 */

const UTM_STORAGE_KEY = "valor.utm";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid", "li_fat_id"];

/** Capture UTMs from the current URL and persist for the session. */
export function captureUtmFromUrl() {
  if (typeof window === "undefined") return null;
  try {
    const url = new URL(window.location.href);
    const found = {};
    UTM_KEYS.forEach((k) => {
      const v = url.searchParams.get(k);
      if (v) found[k] = v;
    });
    if (Object.keys(found).length === 0) return null;
    // First-touch wins for the session — don't overwrite an existing payload.
    const existing = readStoredUtm();
    if (!existing) {
      const payload = {
        ...found,
        landing_page: url.pathname + url.search,
        referrer: document.referrer || null,
        captured_at: new Date().toISOString(),
      };
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(payload));
      return payload;
    }
    return existing;
  } catch {
    return null;
  }
}

/** Read the persisted UTM payload (or null). */
export function readStoredUtm() {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Build the attribution payload to attach to a form submit. */
export function getLeadAttribution() {
  if (typeof window === "undefined") return {};
  const utm = readStoredUtm() || {};
  return {
    ...utm,
    referrer: utm.referrer || (typeof document !== "undefined" ? document.referrer : "") || null,
    landing_page: utm.landing_page || (typeof window !== "undefined" ? window.location.pathname : ""),
    page: typeof window !== "undefined" ? window.location.pathname : "",
  };
}

/**
 * Map of canonical event name → LinkedIn conversion ID.
 *
 * Each entry here means: when this event fires, automatically also call
 *   lintrk("track", { conversion_id: <id> })
 * so the LinkedIn conversion records — no need for callers to pass the
 * conversion ID, and no need to migrate the LinkedIn tag into GTM.
 *
 * Add more entries here as new LinkedIn conversions are created in
 * Campaign Manager → Account Assets → Conversions.
 */
const LINKEDIN_CONVERSIONS = {
  program_brief_submit: 26589404,
  rfq_submit: 26589412,
  contact_submit: 26589420,
  book_call_click: 26589428,
  spec_pdf_request: 26589436,
};

/**
 * Fire a conversion event to every connected sink.
 * Sinks: GTM dataLayer (so any tag managed inside GTM can see it),
 *        PostHog (always, if loaded), GA4 (if gtag loaded),
 *        LinkedIn (if lintrk loaded).
 */
export function trackEvent(name, props = {}) {
  if (typeof window === "undefined") return;
  trackMicrosoftConversion(name, props);
  const payload = { ...getLeadAttribution(), ...props };

  // GTM dataLayer — lets GTM-managed tags (LinkedIn, Meta, MS Ads, etc.)
  // listen for `event: <name>` triggers. Always pushed regardless of which
  // sinks are loaded, so future tag wiring is purely a GTM-UI exercise.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: name, ...payload });

  // PostHog
  if (window.posthog && typeof window.posthog.capture === "function") {
    try { window.posthog.capture(name, payload); } catch (e) { if (typeof console !== "undefined" && console.debug) { console.debug("[analytics] event swallowed", e); } }
  }
  // GA4 (lights up once REACT_APP_GA4_ID is configured)
  if (typeof window.gtag === "function") {
    try { window.gtag("event", name, payload); } catch (e) { if (typeof console !== "undefined" && console.debug) { console.debug("[analytics] event swallowed", e); } }
  }
  // LinkedIn Insight Tag — conversion tracking. Two paths:
  //  1. Event has a mapped LinkedIn conversion ID → fire that conversion.
  //  2. Caller passed an explicit `linkedin_conversion_id` in props → fire that.
  if (typeof window.lintrk === "function") {
    const mapped = LINKEDIN_CONVERSIONS[name];
    if (mapped) {
      try { window.lintrk("track", { conversion_id: mapped }); } catch (e) { if (typeof console !== "undefined" && console.debug) { console.debug("[analytics] event swallowed", e); } }
    } else if (props.linkedin_conversion_id) {
      try { window.lintrk("track", { conversion_id: props.linkedin_conversion_id }); } catch (e) { if (typeof console !== "undefined" && console.debug) { console.debug("[analytics] event swallowed", e); } }
    }
  }
}

/**
 * Convenience wrappers for the conversion moments that matter.
 * Event names below are the canonical identifiers used to create:
 *   - GA4 conversion events (Admin → Events → mark as conversion)
 *   - LinkedIn conversions (Campaign Manager → Conversions → "Internal Event Name")
 *   - GTM triggers (Custom Event → exact match on event name)
 */
export const track = {
  // Generic lead capture (Inline lead form, NSN search bar).
  leadSubmit: (extra = {}) => trackEvent("lead_submit", extra),
  // Contact page form — any inquiry type. Use `programBriefSubmit` for program-office submissions specifically.
  contactSubmit: (extra = {}) => trackEvent("contact_submit", extra),
  // Contact page form when type === "program" — high-intent program-office brief.
  programBriefSubmit: (extra = {}) => trackEvent("program_brief_submit", extra),
  // 60-second RFQ form (Energetics, Mounts pages).
  rfqSubmit: (extra = {}) => trackEvent("rfq_submit", extra),
  // "Book a 30-min call" / "Schedule a meeting" CTAs (Microsoft Bookings).
  bookCallClick: (extra = {}) => trackEvent("book_call_click", extra),
  // Spec sheet PDF gated download.
  specPdfRequest: (extra = {}) => trackEvent("spec_pdf_request", extra),
  // Newsletter signup (footer).
  newsletterSubscribe: (extra = {}) => trackEvent("newsletter_subscribe", extra),
};
